import CryptoJS, { WordArray } from 'crypto-js';

export interface JWT {
  encode(header: JWTHeader, payload: JWTPayload, secret: string | WordArray, algorithm?: Algorithm): string;
}

export interface JWTHeader {
  /**
   * Represents the media type of complete JWT
   */
  typ: string;
  /**
   * The name of the algorithm used to sign the JWT
   */
  alg: string;
  /**
   * Used to define structural information about JWT
   */
  cty?: string;
  /**
   * The Key Id used
   */
  kid?: string;
}

export interface JWTPayload {
  /**
   * The issuer claim
   * This identifies the principal that issued the JWT.
   */
  iss?: string;
  /**
   * The "sub" (subject) claim identifies the principal that is the
 subject of the JWT.
   */
  sub?: string;
  /**
   * The "exp" (expiration time) claim identifies the expiration time.
   */
  exp?: number;
  /**
   * The "nbf" (not before) claim identifies the time before which the JWT
 MUST NOT be accepted for processing.
   */
  nbf?: number;
  /**
   * The "iat" (issued at) claim identifies the time at which the JWT was
 issued.
   */
  iat?: number;
  /**
   * The "jti" (JWT ID) claim provides a unique identifier for the JWT.
   */
  jti?: string;
  /**
   * The "aud" (audience) claim identifies the recipients that the JWT is
 intended for.
   */
  aud?: string;
}

export enum Algorithm {
  HS256 = 'HS256',
  HS384 = 'HS384',
  HS512 = 'HS512',
}

const base64urlEscape = (str: string): string => {
  return str.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

const sign = (input: string, secret: string | WordArray, algo: Algorithm): string => {
  let hmac;
  switch (algo) {
    case Algorithm.HS256:
      hmac = CryptoJS.HmacSHA256(input, secret);
      break;
    case Algorithm.HS384:
      hmac = CryptoJS.HmacSHA384(input, secret);
      break;
    case Algorithm.HS512:
      hmac = CryptoJS.HmacSHA512(input, secret);
      break;
  }
  return base64urlEscape(hmac.toString(CryptoJS.enc.Base64));
};

const base64urlEncode = (str: string): string => {
  return base64urlEscape(CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(str)));
};

/**
 * API to generate JWT token
 *
 * @param header The header of the JWT
 * @param payload The payload or claim of the JWT
 * @param secret The algorithm used to sign the JWT
 * @param algorithm The algorithm used to sign the JWT
 */
const encode = (
  header: JWTHeader,
  payload: JWTPayload,
  secret: string | WordArray,
  algorithm: Algorithm = Algorithm.HS256,
): string => {
  const base64EncodedHeader = base64urlEncode(JSON.stringify(header));
  const base64EncodedPayload = base64urlEncode(JSON.stringify(payload));

  // create segments, all segments should be base64 string
  const segments = [];
  segments.push(base64EncodedHeader);
  segments.push(base64EncodedPayload);
  const signature = sign(segments.join('.'), secret, algorithm);
  segments.push(signature);

  return segments.join('.');
};

export const JWTUtil: JWT = {
  encode: encode,
};
